/**
 * MessageBubble — renders one chat turn.
 *
 * Features:
 *   - Markdown (bold, lists, inline code, tables) via `react-markdown`.
 *   - Click-through enrichment: any phrase matching a Vooxy lead/customer
 *     id surface pattern is turned into a Link to that detail page.
 *   - Tool-call tracelet below the assistant message (toggleable).
 *   - User bubbles align right (emerald); assistant align left (slate).
 *
 * The link enrichment runs AFTER markdown rendering so we don't accidentally
 * mangle Claude's table syntax — we post-process the DOM via a small
 * `renderers` override on each paragraph/table-cell.
 */

import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, useParams } from 'react-router-dom';
import { CaretDown, Sparkle, Wrench, User, Copy } from '@phosphor-icons/react';
import ActionCard from './ActionCard';

// A UUID v4 regex — matches the ids Vooxy assigns to leads/customers/quotes.
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Walk a React children tree and replace plain UUID occurrences with
 * <Link>s to the detail page. The entity-type is inferred from the
 * nearest neighbour tool-call (passed in via `linkContext`).
 */
function enrichIds(children, linkContext, tenantPath) {
  if (!children) return children;
  if (typeof children === 'string') {
    const parts = [];
    let last = 0;
    for (const m of children.matchAll(UUID_RE)) {
      if (m.index > last) parts.push(children.slice(last, m.index));
      const id = m[0];
      const type = linkContext?.get(id) || 'leads';   // sensible default
      parts.push(
        <Link
          key={m.index}
          to={`${tenantPath}/${type}/${id}`}
          className="text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
        >
          {id.slice(0, 8)}…
        </Link>
      );
      last = m.index + id.length;
    }
    if (last < children.length) parts.push(children.slice(last));
    return parts.length === 1 ? parts[0] : parts;
  }
  if (Array.isArray(children)) return children.map((c, i) => <React.Fragment key={i}>{enrichIds(c, linkContext, tenantPath)}</React.Fragment>);
  return children;
}


export default function MessageBubble({ msg, onConfirmAction, onCancelAction }) {
  const { tenantSlug } = useParams();
  const tenantPath = tenantSlug ? `/t/${tenantSlug}` : '';
  const [showTools, setShowTools] = useState(false);
  const [copied, setCopied] = useState(false);

  const isUser = msg.role === 'user';

  // Build a map { uuid -> entity_type } from any tool_calls on this message
  // so that enrichIds can emit the correct Link target. We derive the type
  // from the tool name (search_leads → "leads", search_customers → "customers", …).
  const linkContext = useMemo(() => {
    const m = new Map();
    for (const tc of (msg.tool_calls || [])) {
      const name = tc.name || '';
      const toolType = name.includes('customer') ? 'customers'
        : name.includes('quotation') ? 'quotations'
        : name.includes('interaction') ? 'interactions'
        : 'leads';
      // We don't have the raw IDs returned from the tool here — enrichment
      // works on strings Claude emits inline. The map lets us pick the
      // right entity type when IDs do appear.
      m.set(toolType, toolType);  // keyed by type so default fallback works
    }
    return m;
  }, [msg.tool_calls]);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  const bubbleCls = isUser
    ? 'bg-emerald-600 text-white rounded-2xl rounded-br-md ml-auto'
    : 'bg-slate-50 text-slate-800 border border-slate-200 rounded-2xl rounded-bl-md mr-auto';

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start group`}>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isUser ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-600 text-white'}`}>
        {isUser ? <User size={14} weight="fill" /> : <Sparkle size={14} weight="fill" />}
      </div>
      <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${bubbleCls}`} data-testid={`copilot-msg-${msg.role}`}>
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
        ) : (
          <div className="prose prose-sm max-w-none prose-slate prose-table:border prose-th:bg-slate-100 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-td:border prose-th:border">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Enrich UUIDs → links inside every text node.
                p: ({ children }) => <p>{enrichIds(children, linkContext, tenantPath)}</p>,
                td: ({ children }) => <td>{enrichIds(children, linkContext, tenantPath)}</td>,
                li: ({ children }) => <li>{enrichIds(children, linkContext, tenantPath)}</li>,
                code: ({ inline, children, ...props }) =>
                  inline
                    ? <code className="px-1 py-0.5 rounded bg-slate-200 text-slate-800 text-[12px]" {...props}>{children}</code>
                    : <pre className="rounded bg-slate-900 text-slate-100 text-[12px] p-2 overflow-x-auto"><code {...props}>{children}</code></pre>,
                a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-emerald-700 underline">{children}</a>,
              }}
            >
              {msg.content || ''}
            </ReactMarkdown>
          </div>
        )}

        {!isUser && (msg.actions || []).map((a) => (
          <ActionCard key={a.id} action={a} onConfirm={onConfirmAction} onCancel={onCancelAction} />
        ))}

        {/* Tool-call tracelet (assistant only) */}
        {!isUser && (msg.tool_calls || []).length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500">
            <button
              onClick={() => setShowTools((s) => !s)}
              className="inline-flex items-center gap-1 hover:text-slate-700"
              data-testid="copilot-msg-tools-toggle"
            >
              <Wrench size={11} /> Used {msg.tool_calls.length} tool{msg.tool_calls.length > 1 ? 's' : ''}
              <CaretDown size={10} className={`transition-transform ${showTools ? 'rotate-180' : ''}`} />
            </button>
            {showTools && (
              <ul className="mt-1 pl-3 space-y-0.5">
                {msg.tool_calls.map((tc, i) => (
                  <li key={i}>
                    <span className="font-mono text-emerald-700">{tc.name}</span>
                    {tc.result_summary && <span className="text-slate-400"> — {tc.result_summary}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {!isUser && (
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          title={copied ? 'Copied!' : 'Copy'}
          data-testid="copilot-msg-copy"
        >
          <Copy size={13} />
        </button>
      )}
    </div>
  );
}
